import { supabase } from '@/lib/supabase';

// TypeScript interfaces matching database schema
export interface ProjectMetric {
  label: string;
  value: string;
  icon: string;
}

export interface ClientTestimonial {
  quote: string;
  author: string;
  position: string;
}

export interface ProjectCaseStudy {
  id: string;
  userId: string;
  title: string;
  category: 'process_improvement' | 'compliance_initiative' | 'team_development' | 'technology_implementation';
  industry: string;
  description: string;
  imageUrl: string | null;
  imageAlt: string | null;
  metrics: ProjectMetric[];
  tags: string[];
  challenge: string;
  solution: string;
  results: string[];
  timeline: string | null;
  teamSize: string | null;
  technologies: string[];
  clientTestimonial: ClientTestimonial | null;
  status: 'draft' | 'published' | 'archived';
  isVisible: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

// Database row type (snake_case)
interface ProjectCaseStudyRow {
  id: string;
  user_id: string;
  title: string;
  category: string;
  industry: string;
  description: string;
  image_url: string | null;
  image_alt: string | null;
  metrics: ProjectMetric[];
  tags: string[];
  challenge: string;
  solution: string;
  results: string[];
  timeline: string | null;
  team_size: string | null;
  technologies: string[];
  client_testimonial: ClientTestimonial | null;
  status: string;
  is_visible: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

// Convert database row to camelCase
const convertToCamelCase = (row: ProjectCaseStudyRow): ProjectCaseStudy => ({
  id: row.id,
  userId: row.user_id,
  title: row.title,
  category: row.category as ProjectCaseStudy['category'],
  industry: row.industry,
  description: row.description,
  imageUrl: row.image_url,
  imageAlt: row.image_alt,
  metrics: row.metrics,
  tags: row.tags,
  challenge: row.challenge,
  solution: row.solution,
  results: row.results,
  timeline: row.timeline,
  teamSize: row.team_size,
  technologies: row.technologies,
  clientTestimonial: row.client_testimonial,
  status: row.status as ProjectCaseStudy['status'],
  isVisible: row.is_visible,
  displayOrder: row.display_order,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

// Convert camelCase to database format
const convertToSnakeCase = (project: Partial<ProjectCaseStudy>) => ({
  user_id: project.userId,
  title: project.title,
  category: project.category,
  industry: project.industry,
  description: project.description,
  image_url: project.imageUrl,
  image_alt: project.imageAlt,
  metrics: project.metrics,
  tags: project.tags,
  challenge: project.challenge,
  solution: project.solution,
  results: project.results,
  timeline: project.timeline,
  team_size: project.teamSize,
  technologies: project.technologies,
  client_testimonial: project.clientTestimonial,
  status: project.status,
  is_visible: project.isVisible,
  display_order: project.displayOrder,
});

// Get all published projects for public view
export const getPublishedProjects = async (): Promise<ProjectCaseStudy[]> => {
  try {
    const { data, error } = await supabase
      .from('project_case_studies')
      .select('*')
      .eq('status', 'published')
      .eq('is_visible', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertToCamelCase);
  } catch (error) {
    console.error('Error fetching published projects:', error);
    throw error;
  }
};

// Get all projects for admin
export const getAllProjects = async (): Promise<ProjectCaseStudy[]> => {
  try {
    const { data, error } = await supabase
      .from('project_case_studies')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertToCamelCase);
  } catch (error) {
    console.error('Error fetching all projects:', error);
    throw error;
  }
};

// Get project by ID
export const getProjectById = async (id: string): Promise<ProjectCaseStudy | null> => {
  try {
    const { data, error } = await supabase
      .from('project_case_studies')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data ? convertToCamelCase(data) : null;
  } catch (error) {
    console.error('Error fetching project:', error);
    throw error;
  }
};

// Create new project
export const createProject = async (project: Partial<ProjectCaseStudy>): Promise<ProjectCaseStudy> => {
  try {
    const dbProject = convertToSnakeCase(project);
    
    const { data, error } = await supabase
      .from('project_case_studies')
      .insert([dbProject])
      .select()
      .single();

    if (error) throw error;
    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

// Update project
export const updateProject = async (id: string, updates: Partial<ProjectCaseStudy>): Promise<ProjectCaseStudy> => {
  try {
    const dbUpdates = convertToSnakeCase(updates);
    
    const { data, error } = await supabase
      .from('project_case_studies')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

// Delete project
export const deleteProject = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('project_case_studies')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// Toggle project visibility
export const toggleProjectVisibility = async (id: string, isVisible: boolean): Promise<void> => {
  try {
    const { error } = await supabase
      .from('project_case_studies')
      .update({ is_visible: isVisible })
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error toggling project visibility:', error);
    throw error;
  }
};

// Update project status
export const updateProjectStatus = async (
  id: string,
  status: 'draft' | 'published' | 'archived'
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('project_case_studies')
      .update({ status })
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating project status:', error);
    throw error;
  }
};

// Get projects by category
export const getProjectsByCategory = async (category: string): Promise<ProjectCaseStudy[]> => {
  try {
    const { data, error } = await supabase
      .from('project_case_studies')
      .select('*')
      .eq('category', category)
      .eq('status', 'published')
      .eq('is_visible', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertToCamelCase);
  } catch (error) {
    console.error('Error fetching projects by category:', error);
    throw error;
  }
};

// Get projects by status
export const getProjectsByStatus = async (status: string): Promise<ProjectCaseStudy[]> => {
  try {
    const { data, error } = await supabase
      .from('project_case_studies')
      .select('*')
      .eq('status', status)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return (data || []).map(convertToCamelCase);
  } catch (error) {
    console.error('Error fetching projects by status:', error);
    throw error;
  }
};

// Upload project image
export const uploadProjectImage = async (file: File): Promise<string> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
    const filePath = `project-images/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('projects')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('projects')
      .getPublicUrl(filePath);

    return data.publicUrl;
  } catch (error) {
    console.error('Error uploading project image:', error);
    throw error;
  }
};